import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ApprovalWorkflow() {
  const [approvals, setApprovals] = useState([]);
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchApprovals();
    const interval = setInterval(fetchApprovals, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchApprovals = async () => {
    try {
      const response = await axios.get(`${API}/approvals?status=pending`);
      setApprovals(response.data);
    } catch (error) {
      console.error('Error fetching approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  const processApproval = async (approvalId, action) => {
    try {
      await axios.post(`${API}/approvals/${approvalId}/action`, {
        approver: 'Current User',
        action: action,
        comments: comments
      });
      
      setDialogOpen(false);
      setComments('');
      fetchApprovals();
    } catch (error) {
      console.error('Error processing approval:', error);
      alert(error.response?.data?.detail || 'Error processing approval');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-lg">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="approval-workflow">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Approval Workflow</h1>
        <p className="text-muted-foreground mt-2">Review and approve deployment requests</p>
      </div>

      {approvals.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="h-12 w-12 mx-auto text-green-500 mb-4" />
            <p className="text-muted-foreground">No pending approvals</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {approvals.map((approval) => (
            <Card key={approval.id} data-testid={`approval-${approval.id}`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline">{approval.ticket_id.slice(0, 8)}</Badge>
                      <Badge className="bg-orange-500">
                        {approval.environment.toUpperCase()} Deployment
                      </Badge>
                      <Badge variant="outline">{approval.status}</Badge>
                    </div>
                    <CardTitle>Deployment Approval Request</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Quality Metrics */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-3 border rounded-lg">
                      <div className="text-sm text-muted-foreground">Code Coverage</div>
                      <div className={`text-2xl font-bold ${approval.code_coverage >= 85 ? 'text-green-600' : 'text-red-600'}`}>
                        {approval.code_coverage.toFixed(1)}%
                      </div>
                      {approval.code_coverage < 85 && (
                        <div className="text-xs text-red-600 mt-1">Below 85% threshold</div>
                      )}
                    </div>
                    
                    <div className="p-3 border rounded-lg">
                      <div className="text-sm text-muted-foreground">Governance Score</div>
                      <div className="text-2xl font-bold text-blue-600">
                        {approval.governance_report?.governance_result?.compliance_score || 'N/A'}
                      </div>
                    </div>
                    
                    <div className="p-3 border rounded-lg">
                      <div className="text-sm text-muted-foreground">Security Score</div>
                      <div className="text-2xl font-bold text-purple-600">
                        {approval.governance_report?.governance_result?.security_score || 'N/A'}
                      </div>
                    </div>
                  </div>

                  {/* Test Results */}
                  {approval.test_results && (
                    <div className="p-3 border rounded-lg bg-slate-50">
                      <div className="font-medium mb-2">Test Results</div>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Total Tests:</span> {approval.test_results.total_tests}
                        </div>
                        <div>
                          <span className="text-muted-foreground">Passed:</span> <span className="text-green-600 font-medium">{approval.test_results.passed}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Failed:</span> <span className="text-red-600 font-medium">{approval.test_results.failed}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Governance Reasoning */}
                  {approval.governance_report?.governance_result?.reasoning && (
                    <Alert>
                      <AlertDescription>
                        <strong>Governance Assessment:</strong> {approval.governance_report.governance_result.reasoning}
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Quality Gate Warning */}
                  {approval.environment === 'prod' && approval.code_coverage < 85 && (
                    <Alert variant="destructive">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        Production deployment requires minimum 85% code coverage. Current: {approval.code_coverage.toFixed(1)}%
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Dialog open={dialogOpen && selectedApproval?.id === approval.id} onOpenChange={(open) => {
                      setDialogOpen(open);
                      if (open) setSelectedApproval(approval);
                    }}>
                      <DialogTrigger asChild>
                        <Button 
                          className="flex-1" 
                          variant="default"
                          disabled={approval.environment === 'prod' && approval.code_coverage < 85}
                          data-testid={`approve-btn-${approval.id}`}
                        >
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Approve
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Approve Deployment</DialogTitle>
                          <DialogDescription>
                            Confirm approval for {approval.environment.toUpperCase()} deployment
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label htmlFor="comments">Comments (Optional)</Label>
                            <Textarea
                              id="comments"
                              value={comments}
                              onChange={(e) => setComments(e.target.value)}
                              placeholder="Add any comments or notes..."
                              rows={3}
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                            <Button onClick={() => processApproval(approval.id, 'approve')} className="flex-1">
                              Confirm Approval
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>

                    <Button 
                      variant="destructive" 
                      onClick={() => {
                        setSelectedApproval(approval);
                        setDialogOpen(true);
                      }}
                      data-testid={`reject-btn-${approval.id}`}
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      Reject
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
